import m from 'mithril'
import { css } from '@emotion/css'
import { colors, font, space, card, input, btnSecondary } from '../styles/theme.js'
import { evalSummary as evalSummaryApi, tags as tagsApi, rubrics as rubricsApi } from '../lib/api.js'
import { GRADE_COLORS, CRITICALITY_COLORS, FULFILLMENT_LABELS } from '../components/eval-panel.js'

// --- Styles ---

const breadcrumb = css`
  font-size: ${font.size.sm};
  color: ${colors.textMuted};
  margin-bottom: ${space.sm};
  a { color: ${colors.textSecondary}; }
`

const pageTitle = css`
  font-size: ${font.size['2xl']};
  font-weight: ${font.weight.bold};
  margin-bottom: ${space.lg};
`

const toolbar = css`
  display: flex;
  align-items: center;
  gap: ${space.md};
  padding: ${space.md} 0;
  flex-shrink: 0;
  flex-wrap: wrap;
`

const searchInput = css`
  ${input};
  max-width: 400px;
`

const sortSelect = css`
  padding: ${space.sm} ${space.md};
  border: 1px solid ${colors.border};
  border-radius: 6px;
  font-size: ${font.size.sm};
  font-family: ${font.family};
  background: ${colors.bgCard};
  color: ${colors.text};
  cursor: pointer;
`

const toggleFiltersBtn = css`
  padding: ${space.sm} ${space.md};
  border: 1px solid ${colors.border};
  border-radius: 6px;
  font-size: ${font.size.sm};
  font-family: ${font.family};
  background: ${colors.bgCard};
  color: ${colors.text};
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  &:hover { background: ${colors.bg}; }
`

const activeFilterCount = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: ${colors.brand};
  color: white;
  font-size: 10px;
  font-weight: ${font.weight.semibold};
  border-radius: 50%;
  width: 16px;
  height: 16px;
  margin-left: 4px;
`

const filterPanelStyle = css`
  display: flex;
  align-items: center;
  gap: ${space.sm};
  padding: ${space.sm} 0;
  flex-shrink: 0;
  flex-wrap: wrap;
`

const filterLabel = css`
  font-size: ${font.size.xs};
  color: ${colors.textMuted};
  font-weight: ${font.weight.medium};
`

const filterInput = css`
  padding: 4px ${space.sm};
  border: 1px solid ${colors.border};
  border-radius: 4px;
  font-size: ${font.size.xs};
  font-family: ${font.family};
  background: ${colors.bgCard};
  color: ${colors.text};
  width: 130px;
  &:focus { border-color: ${colors.borderFocus}; outline: none; }
`

const filterDivider = css`
  width: 1px;
  height: 20px;
  background: ${colors.border};
  margin: 0 ${space.xs};
`

const clearFiltersBtn = css`
  padding: 3px ${space.sm};
  border: 1px solid ${colors.border};
  border-radius: 4px;
  font-size: ${font.size.xs};
  font-family: ${font.family};
  background: none;
  color: ${colors.textMuted};
  cursor: pointer;
  &:hover { color: ${colors.text}; background: ${colors.bg}; }
`

// Tag filter styles
const tagFilterContainer = css`position: relative;`
const tagFilterBtn = css`
  padding: ${space.sm} ${space.md};
  border: 1px solid ${colors.border};
  border-radius: 6px;
  font-size: ${font.size.sm};
  font-family: ${font.family};
  background: ${colors.bgCard};
  color: ${colors.text};
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  &:hover { background: ${colors.bg}; }
`
const tagDropdown = css`
  position: absolute; top: 100%; left: 0; margin-top: 4px;
  width: 280px; background: ${colors.bgCard};
  border: 1px solid ${colors.border}; border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1); z-index: 50; overflow: hidden;
`
const tagSearchInput = css`
  width: 100%; padding: ${space.sm} ${space.md};
  border: none; border-bottom: 1px solid ${colors.border};
  font-size: ${font.size.sm}; font-family: ${font.family};
  background: transparent; color: ${colors.text}; outline: none;
  box-sizing: border-box;
  &::placeholder { color: ${colors.textMuted}; }
`
const tagDropdownList = css`max-height: 240px; overflow-y: auto;`
const tagDropdownItem = css`
  display: flex; align-items: center; gap: ${space.sm};
  padding: 6px ${space.md}; cursor: pointer; font-size: ${font.size.sm};
  transition: background 0.08s;
  &:hover { background: ${colors.bg}; }
`
const tagDropdownSwatch = css`width: 10px; height: 10px; border-radius: 3px; flex-shrink: 0;`
const tagDropdownName = css`flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;`
const tagStateIndicator = css`
  font-size: 11px; font-weight: ${font.weight.semibold};
  width: 18px; height: 18px; border-radius: 4px;
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
`
const selectedTagChips = css`
  display: flex; align-items: center; gap: 4px; flex-wrap: wrap; margin-bottom: ${space.sm};
`
const selectedTagChip = css`
  display: inline-flex; align-items: center; gap: 3px;
  padding: 2px 8px; border-radius: 10px; font-size: ${font.size.xs};
  font-weight: ${font.weight.medium}; cursor: pointer; user-select: none;
  &:hover { opacity: 0.8; }
`
const tagModeToggle = css`
  font-size: ${font.size.xs}; color: ${colors.textMuted}; cursor: pointer;
  padding: 2px 6px; border-radius: 4px; border: 1px solid ${colors.border};
  background: ${colors.bgCard};
  &:hover { color: ${colors.text}; }
`

// Rubric selector
const rubricSelectorContainer = css`position: relative;`
const rubricSelectorBtn = css`
  padding: ${space.sm} ${space.md};
  border: 1px solid ${colors.border};
  border-radius: 6px;
  font-size: ${font.size.sm};
  font-family: ${font.family};
  background: ${colors.bgCard};
  color: ${colors.text};
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  &:hover { background: ${colors.bg}; }
`
const rubricDropdown = css`
  position: absolute; top: 100%; left: 0; margin-top: 4px;
  width: 300px; background: ${colors.bgCard};
  border: 1px solid ${colors.border}; border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1); z-index: 50; overflow: hidden;
`
const rubricDropdownList = css`max-height: 300px; overflow-y: auto;`
const rubricCheckItem = css`
  display: flex; align-items: center; gap: ${space.sm};
  padding: 8px ${space.md}; cursor: pointer; font-size: ${font.size.sm};
  transition: background 0.08s;
  &:hover { background: ${colors.bg}; }
`
const rubricCheckbox = css`
  width: 14px; height: 14px; border-radius: 3px;
  border: 1.5px solid ${colors.border}; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
  font-size: 10px; color: white;
`

// Summary card styles
const summaryMeta = css`
  font-size: ${font.size.sm};
  color: ${colors.textMuted};
  margin-bottom: ${space.lg};
`

const rubricSection = css`
  ${card};
  margin-bottom: ${space.lg};
`

const rubricHeader = css`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: ${space.md};
`

const rubricTitle = css`
  font-size: ${font.size.lg};
  font-weight: ${font.weight.semibold};
`

const sessionCountBadge = css`
  font-size: ${font.size.sm};
  color: ${colors.textMuted};
`

const versionChipRow = css`
  display: flex;
  gap: 6px;
  margin-bottom: ${space.sm};
`

const versionChip = css`
  padding: 2px 10px;
  border-radius: 10px;
  font-size: ${font.size.xs};
  font-weight: ${font.weight.medium};
  cursor: pointer;
  user-select: none;
  border: 1px solid ${colors.border};
  background: ${colors.bgCard};
  color: ${colors.textSecondary};
  &:hover { opacity: 0.8; }
`

const versionChipActive = css`
  background: ${colors.brand};
  color: white;
  border-color: ${colors.brand};
`

const versionSubHeader = css`
  font-size: ${font.size.xs};
  color: ${colors.textMuted};
  font-weight: ${font.weight.semibold};
  padding: ${space.sm} 0 4px;
`

const criterionRow = css`
  display: flex;
  align-items: center;
  gap: ${space.sm};
  padding: ${space.sm} 0;
  border-bottom: 1px solid ${colors.border};
  &:last-child { border-bottom: none; }
`

const critDot = css`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
`

const critDesc = css`
  flex: 0 0 380px;
  font-size: ${font.size.sm};
  color: ${colors.text};
  min-width: 0;
`

const barContainer = css`
  flex: 1;
  height: 20px;
  display: flex;
  border-radius: 4px;
  overflow: hidden;
  background: ${colors.border};
  position: relative;
`

const barSegment = css`
  height: 100%;
  transition: width 0.2s ease;
  position: relative;
`

const barLabel = css`
  font-size: 10px;
  font-weight: ${font.weight.semibold};
  color: white;
  text-shadow: 0 0 2px rgba(0,0,0,0.3);
  line-height: 1;
`

const tooltipStyle = css`
  position: absolute;
  bottom: calc(100% + 6px);
  left: 50%;
  transform: translateX(-50%);
  background: ${colors.text};
  color: white;
  font-size: 11px;
  padding: 4px 8px;
  border-radius: 4px;
  white-space: nowrap;
  pointer-events: none;
  z-index: 100;
`

const emptyState = css`
  text-align: center;
  padding: ${space['2xl']};
  color: ${colors.textMuted};
`

const countLabel = css`
  flex-shrink: 0;
  font-size: ${font.size.xs};
  color: ${colors.textMuted};
  width: 40px;
  text-align: right;
`

export function EvalSummaryView() {
  let projectId = null
  let loading = false
  let summaryData = null
  let abortController = null

  // Filter state (mirrors browser.js)
  let search = ''
  let searchDebounce = null
  let includeTagIds = new Set()
  let excludeTagIds = new Set()
  let tagMode = 'any'
  let allTags = []
  let dateFrom = ''
  let dateTo = ''
  let minMessages = ''
  let maxMessages = ''
  let filterUntagged = false
  let filterBookmarked = false
  let filterMyActivity = false
  let evalFilter = 'any'
  let evalRubricId = ''

  // Rubric selector state
  let rubricList = []
  let selectedRubricIds = new Set()
  let rubricDropdownOpen = false

  // Tag dropdown state
  let tagDropdownOpen = false
  let tagSearch = ''

  // Advanced filters
  let showFilters = false

  // Version selection: rubricId -> 'all' | versionId
  let selectedVersions = new Map()

  // Tooltip state
  let hoveredSegment = null

  async function loadTags() {
    try { allTags = await tagsApi.list(projectId) } catch { allTags = [] }
  }

  async function loadRubrics() {
    try { rubricList = await rubricsApi.list(projectId) } catch { rubricList = [] }
  }

  function buildParams() {
    const params = {}
    if (search.trim()) params.search = search.trim()
    if (includeTagIds.size > 0) {
      params.tagIds = [...includeTagIds].join(',')
      params.tagMode = tagMode
    }
    if (excludeTagIds.size > 0) params.excludeTagIds = [...excludeTagIds].join(',')
    if (dateFrom) params.dateFrom = dateFrom
    if (dateTo) params.dateTo = dateTo + 'T23:59:59'
    if (minMessages !== '' && !isNaN(parseInt(minMessages, 10))) params.minMessages = parseInt(minMessages, 10)
    if (maxMessages !== '' && !isNaN(parseInt(maxMessages, 10))) params.maxMessages = parseInt(maxMessages, 10)
    if (filterUntagged) params.untagged = '1'
    if (filterBookmarked) params.bookmarked = '1'
    if (filterMyActivity) params.myActivity = '1'
    if (evalFilter === 'evaluated') params.evaluated = 'yes'
    else if (evalFilter === 'unevaluated') params.evaluated = 'no'
    if (evalRubricId) params.evalRubricId = evalRubricId
    if (selectedRubricIds.size > 0) params.rubricIds = [...selectedRubricIds].join(',')
    return params
  }

  async function loadSummary() {
    if (abortController) abortController.abort()
    abortController = new AbortController()
    loading = true
    m.redraw()
    try {
      summaryData = await evalSummaryApi.get(projectId, buildParams(), { signal: abortController.signal })
      // Initialize version selections for rubrics with multiple versions
      if (summaryData && summaryData.rubrics) {
        const existingIds = new Set(summaryData.rubrics.map(r => r.id))
        // Remove stale entries
        for (const key of selectedVersions.keys()) {
          if (!existingIds.has(key)) selectedVersions.delete(key)
        }
        // Default new multi-version rubrics to 'all'
        for (const r of summaryData.rubrics) {
          if (r.versions && r.versions.length > 1 && !selectedVersions.has(r.id)) {
            selectedVersions.set(r.id, 'all')
          }
        }
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('Failed to load eval summary:', err)
        summaryData = null
      }
    } finally {
      loading = false
      m.redraw()
    }
  }

  function onSearch(value) {
    search = value
    clearTimeout(searchDebounce)
    searchDebounce = setTimeout(loadSummary, 300)
  }

  function applyFilters() {
    loadSummary()
  }

  function clearAllFilters() {
    dateFrom = ''
    dateTo = ''
    minMessages = ''
    maxMessages = ''
    filterUntagged = false
    filterBookmarked = false
    filterMyActivity = false
    includeTagIds = new Set()
    excludeTagIds = new Set()
    tagMode = 'any'
    evalFilter = 'any'
    evalRubricId = ''
    loadSummary()
  }

  function activeFilterCountNum() {
    let count = 0
    if (dateFrom) count++
    if (dateTo) count++
    if (minMessages !== '') count++
    if (maxMessages !== '') count++
    if (filterUntagged) count++
    if (filterBookmarked) count++
    if (filterMyActivity) count++
    if (includeTagIds.size > 0) count++
    if (excludeTagIds.size > 0) count++
    if (evalFilter !== 'any') count++
    return count
  }

  // Tag filter helpers
  function cycleTag(tagId) {
    if (includeTagIds.has(tagId)) {
      includeTagIds.delete(tagId)
      excludeTagIds.add(tagId)
    } else if (excludeTagIds.has(tagId)) {
      excludeTagIds.delete(tagId)
    } else {
      includeTagIds.add(tagId)
    }
    filterUntagged = false
    loadSummary()
  }

  function removeTagFilter(tagId) {
    includeTagIds.delete(tagId)
    excludeTagIds.delete(tagId)
    loadSummary()
  }

  function getTagState(tagId) {
    if (includeTagIds.has(tagId)) return 'include'
    if (excludeTagIds.has(tagId)) return 'exclude'
    return 'off'
  }

  function selectedTags() {
    return allTags.filter(t => includeTagIds.has(t.id) || excludeTagIds.has(t.id))
  }

  function filteredTags() {
    if (!tagSearch.trim()) return allTags
    const q = tagSearch.toLowerCase()
    return allTags.filter(t => t.name.toLowerCase().includes(q))
  }

  function buildBrowserLink(rubricId) {
    const params = new URLSearchParams()
    if (includeTagIds.size > 0) params.set('tags', [...includeTagIds].join(','))
    if (excludeTagIds.size > 0) params.set('xtags', [...excludeTagIds].join(','))
    if (tagMode !== 'any') params.set('tagMode', tagMode)
    if (dateFrom) params.set('from', dateFrom)
    if (dateTo) params.set('to', dateTo)
    if (minMessages !== '') params.set('minMsg', minMessages)
    if (maxMessages !== '') params.set('maxMsg', maxMessages)
    if (filterUntagged) params.set('untagged', '1')
    if (filterBookmarked) params.set('bm', '1')
    if (filterMyActivity) params.set('mine', '1')
    params.set('eval', 'evaluated')
    params.set('rubric', rubricId)
    const qs = params.toString()
    return `/projects/${projectId}/browser${qs ? '?' + qs : ''}`
  }

  function toggleRubric(rubricId) {
    if (selectedRubricIds.has(rubricId)) {
      selectedRubricIds.delete(rubricId)
    } else {
      selectedRubricIds.add(rubricId)
    }
    loadSummary()
  }

  function renderTagFilter() {
    const selected = selectedTags()
    const hasTagFilters = selected.length > 0

    return m('div', { class: tagFilterContainer }, [
      m('button', {
        class: tagFilterBtn,
        style: hasTagFilters ? `border-color: ${colors.brand};` : '',
        onclick: (e) => { e.stopPropagation(); tagDropdownOpen = !tagDropdownOpen; tagSearch = '' },
      }, [
        'Tags',
        hasTagFilters ? m('span', { class: activeFilterCount }, selected.length) : null,
      ]),
      tagDropdownOpen
        ? m('div', { class: tagDropdown, onclick: (e) => e.stopPropagation() }, [
            m('input', {
              class: tagSearchInput,
              type: 'text',
              placeholder: 'Search tags...',
              value: tagSearch,
              oninput: (e) => { tagSearch = e.target.value },
              oncreate: (vnode) => { vnode.dom.focus() },
              onkeydown: (e) => { if (e.key === 'Escape') { tagDropdownOpen = false; e.stopPropagation() } },
            }),
            m('div', { class: tagDropdownList }, [
              m('div', {
                key: 'untagged',
                class: tagDropdownItem,
                onclick: () => {
                  filterUntagged = !filterUntagged
                  if (filterUntagged) { includeTagIds = new Set(); excludeTagIds = new Set() }
                  loadSummary()
                },
              }, [
                m('div', { class: tagDropdownSwatch, style: `background: ${colors.textMuted};` }),
                m('span', { class: tagDropdownName }, 'Untagged'),
                filterUntagged
                  ? m('span', { class: tagStateIndicator, style: `background: ${colors.brand}; color: white;` }, '\u2713')
                  : null,
              ]),
              ...filteredTags().map(t => {
                const state = getTagState(t.id)
                return m('div', {
                  key: t.id,
                  class: tagDropdownItem,
                  onclick: () => cycleTag(t.id),
                }, [
                  m('div', { class: tagDropdownSwatch, style: `background: ${t.color || colors.border};` }),
                  m('span', { class: tagDropdownName }, t.name),
                  state === 'include'
                    ? m('span', { class: tagStateIndicator, style: `background: #a9e34b30; color: #5a8a00;` }, '+')
                    : state === 'exclude'
                      ? m('span', { class: tagStateIndicator, style: `background: ${colors.critical}20; color: ${colors.critical};` }, '\u2212')
                      : null,
                ])
              }),
            ]),
          ])
        : null,
    ])
  }

  function renderSelectedChips() {
    const selected = selectedTags()
    if (selected.length === 0 && !filterUntagged) return null
    const chips = []
    if (filterUntagged) {
      chips.push(m('span', {
        key: 'untagged',
        class: selectedTagChip,
        style: `background: ${colors.textMuted}20; color: ${colors.textMuted};`,
        onclick: () => { filterUntagged = false; loadSummary() },
      }, ['Untagged', ' \u00d7']))
    }
    if (selected.length > 1 && includeTagIds.size > 1) {
      chips.push(m('span', {
        key: 'mode',
        class: tagModeToggle,
        onclick: () => { tagMode = tagMode === 'any' ? 'all' : 'any'; loadSummary() },
        title: `Switch to ${tagMode === 'any' ? 'ALL' : 'ANY'} match mode`,
      }, tagMode === 'any' ? 'ANY' : 'ALL'))
    }
    for (const t of selected) {
      const state = getTagState(t.id)
      const bg = state === 'exclude' ? `${colors.critical}15` : `${t.color || colors.border}25`
      const fg = state === 'exclude' ? colors.critical : (t.color || colors.textSecondary)
      chips.push(m('span', {
        key: t.id,
        class: selectedTagChip,
        style: `background: ${bg}; color: ${fg};`,
        onclick: () => removeTagFilter(t.id),
      }, [
        state === 'exclude' ? '\u2212 ' : '',
        t.name,
        ' \u00d7',
      ]))
    }
    return m('div', { class: selectedTagChips }, chips)
  }

  function renderRubricSelector() {
    const count = selectedRubricIds.size
    return m('div', { class: rubricSelectorContainer }, [
      m('button', {
        class: rubricSelectorBtn,
        style: count > 0 ? `border-color: ${colors.brand};` : '',
        onclick: (e) => { e.stopPropagation(); rubricDropdownOpen = !rubricDropdownOpen },
      }, [
        'Rubrics',
        count > 0 ? m('span', { class: activeFilterCount }, count) : null,
      ]),
      rubricDropdownOpen
        ? m('div', { class: rubricDropdown, onclick: (e) => e.stopPropagation() }, [
            m('div', {
              style: `padding: 6px ${space.md}; font-size: ${font.size.xs}; color: ${colors.textMuted}; border-bottom: 1px solid ${colors.border};`,
            }, count === 0 ? 'Showing all rubrics' : `${count} selected`),
            m('div', { class: rubricDropdownList },
              rubricList.map(r => {
                const checked = selectedRubricIds.has(r.id)
                return m('div', {
                  key: r.id,
                  class: rubricCheckItem,
                  onclick: () => toggleRubric(r.id),
                }, [
                  m('div', {
                    class: rubricCheckbox,
                    style: checked ? `background: ${colors.brand}; border-color: ${colors.brand};` : '',
                  }, checked ? '\u2713' : ''),
                  m('span', { style: 'flex: 1;' }, r.title),
                  m('span', { style: `font-size: ${font.size.xs}; color: ${colors.textMuted};` },
                    `v${r.current_version || 1}`),
                ])
              }),
            ),
          ])
        : null,
    ])
  }

  // Bar chart rendering
  const GRADE_ORDER = ['yes', 'no', 'partial', 'unclear', 'na', 'ungraded']

  function renderBar(criterion, rubricId) {
    const g = criterion.grades
    const total = GRADE_ORDER.reduce((sum, k) => sum + (g[k] || 0), 0)
    if (total === 0) {
      return m('div', { class: barContainer, style: 'opacity: 0.4;' },
        m('div', { style: `width: 100%; height: 100%; background: ${colors.border};` }))
    }

    const segments = []
    for (const key of GRADE_ORDER) {
      const count = g[key] || 0
      if (count === 0) continue
      const pct = (count / total * 100)
      const segKey = `${rubricId}-${criterion.id}-${key}`
      const showLabel = pct >= 8
      segments.push(m('div', {
        key,
        class: barSegment,
        style: `width: ${pct}%; background: ${GRADE_COLORS[key]}; display: flex; align-items: center; justify-content: center; overflow: hidden;`,
        onmouseenter: () => { hoveredSegment = segKey; m.redraw() },
        onmouseleave: () => { hoveredSegment = null; m.redraw() },
      }, [
        showLabel
          ? m('span', { class: barLabel }, count)
          : null,
        hoveredSegment === segKey
          ? m('div', { class: tooltipStyle },
              `${FULFILLMENT_LABELS[key]}: ${count} (${Math.round(pct)}%)`)
          : null,
      ]))
    }
    return m('div', { class: barContainer }, segments)
  }

  // Close dropdowns on outside click
  function onDocClick() {
    let changed = false
    if (tagDropdownOpen) { tagDropdownOpen = false; changed = true }
    if (rubricDropdownOpen) { rubricDropdownOpen = false; changed = true }
    if (changed) m.redraw()
  }

  return {
    oninit(vnode) {
      projectId = vnode.attrs.projectId
      document.addEventListener('click', onDocClick)
      loadTags()
      loadRubrics()
      loadSummary()
    },
    onremove() {
      document.removeEventListener('click', onDocClick)
      if (abortController) abortController.abort()
      clearTimeout(searchDebounce)
    },
    view(vnode) {
      projectId = vnode.attrs.projectId
      const filterCount = activeFilterCountNum()

      return m('div', [
        m('div', { class: breadcrumb }, [
          m(m.route.Link, { href: '/projects' }, 'Projects'),
          ' / ',
          m(m.route.Link, { href: `/projects/${projectId}` }, 'Project'),
          ' / ',
          'Eval Summary',
        ]),

        m('h1', { class: pageTitle }, 'Evaluation Summary'),

        // Toolbar: search + rubric selector + tag filter + filters toggle
        m('div', { class: toolbar }, [
          m('input', {
            class: searchInput,
            type: 'text',
            placeholder: 'Search sessions...',
            value: search,
            oninput: (e) => onSearch(e.target.value),
          }),
          rubricList.length > 0 ? renderRubricSelector() : null,
          allTags.length > 0 ? renderTagFilter() : null,
          m('button', {
            class: toggleFiltersBtn,
            onclick: () => { showFilters = !showFilters },
          }, [
            'Filters',
            filterCount > 0 ? m('span', { class: activeFilterCount }, filterCount) : null,
          ]),
        ]),

        // Selected tag chips
        renderSelectedChips(),

        // Filter panel
        showFilters
          ? m('div', { class: filterPanelStyle }, [
              m('span', { class: filterLabel }, 'Date:'),
              m('input', {
                class: filterInput,
                type: 'date',
                value: dateFrom,
                onchange: (e) => { dateFrom = e.target.value; applyFilters() },
              }),
              m('span', { style: `color: ${colors.textMuted}; font-size: ${font.size.xs};` }, 'to'),
              m('input', {
                class: filterInput,
                type: 'date',
                value: dateTo,
                onchange: (e) => { dateTo = e.target.value; applyFilters() },
              }),
              m('span', { class: filterDivider }),
              m('span', { class: filterLabel }, 'Messages:'),
              m('input', {
                class: filterInput,
                style: 'width: 70px;',
                type: 'number',
                placeholder: 'Min',
                min: 0,
                value: minMessages,
                onchange: (e) => { minMessages = e.target.value; applyFilters() },
              }),
              m('span', { style: `color: ${colors.textMuted}; font-size: ${font.size.xs};` }, '\u2013'),
              m('input', {
                class: filterInput,
                style: 'width: 70px;',
                type: 'number',
                placeholder: 'Max',
                min: 0,
                value: maxMessages,
                onchange: (e) => { maxMessages = e.target.value; applyFilters() },
              }),
              m('span', { class: filterDivider }),
              m('span', { class: filterLabel }, 'Evaluated:'),
              m('select', {
                class: sortSelect,
                style: `font-size: ${font.size.xs}; padding: 4px ${space.sm};`,
                value: evalFilter,
                onchange: (e) => { evalFilter = e.target.value; if (evalFilter === 'any') evalRubricId = ''; loadSummary() },
              }, [
                m('option', { value: 'any' }, 'Any'),
                m('option', { value: 'evaluated' }, 'Evaluated'),
                m('option', { value: 'unevaluated' }, 'Not evaluated'),
              ]),
              m('span', { class: filterDivider }),
              m('button', {
                class: clearFiltersBtn,
                style: filterBookmarked ? `color: ${colors.brand}; border-color: ${colors.brand};` : '',
                onclick: () => { filterBookmarked = !filterBookmarked; loadSummary() },
              }, '\u2606 Bookmarked'),
              m('button', {
                class: clearFiltersBtn,
                style: filterMyActivity ? `color: ${colors.brand}; border-color: ${colors.brand};` : '',
                onclick: () => { filterMyActivity = !filterMyActivity; loadSummary() },
              }, 'My sessions'),
              filterCount > 0
                ? m('button', { class: clearFiltersBtn, onclick: clearAllFilters }, 'Clear filters')
                : null,
            ])
          : null,

        // Summary content
        (() => {
          if (loading) return m('div', { class: emptyState }, 'Loading...')
          const visibleRubrics = summaryData ? summaryData.rubrics.filter(r => r.sessionCount > 0) : []
          if (!summaryData || visibleRubrics.length === 0) {
            return m('div', { class: emptyState }, 'No evaluations match these filters')
          }

          const sections = []
          sections.push(m('div', { class: summaryMeta },
            `${summaryData.totalFilteredSessions} sessions match filters`))

          // Legend
          sections.push(m('div', {
            style: `display: flex; gap: ${space.md}; margin-bottom: ${space.md}; flex-wrap: wrap;`,
          }, GRADE_ORDER.filter(k => k !== 'ungraded').map(key =>
            m('div', {
              key,
              style: `display: flex; align-items: center; gap: 4px; font-size: ${font.size.xs}; color: ${colors.textSecondary};`,
            }, [
              m('div', { style: `width: 10px; height: 10px; border-radius: 2px; background: ${GRADE_COLORS[key]};` }),
              FULFILLMENT_LABELS[key],
            ])
          )))

          // Rubric sections — build children imperatively to avoid mixing keyed/non-keyed
          for (const rubric of visibleRubrics) {
            const children = []
            children.push(m('div', { class: rubricHeader }, [
              m('div', { class: rubricTitle }, rubric.title),
              m(m.route.Link, {
                class: sessionCountBadge,
                href: buildBrowserLink(rubric.id),
              }, `${rubric.sessionCount} session${rubric.sessionCount !== 1 ? 's' : ''} evaluated`),
            ]))

            // Version chips (only if multiple versions)
            const versions = rubric.versions || []
            if (versions.length > 1) {
              const selected = selectedVersions.get(rubric.id) || 'all'
              const chips = []
              chips.push(m('span', {
                class: `${versionChip} ${selected === 'all' ? versionChipActive : ''}`,
                onclick: () => { selectedVersions.set(rubric.id, 'all') },
              }, 'All'))
              for (const v of versions) {
                chips.push(m('span', {
                  class: `${versionChip} ${selected === v.versionId ? versionChipActive : ''}`,
                  onclick: () => { selectedVersions.set(rubric.id, v.versionId) },
                }, `v${v.version}`))
              }
              children.push(m('div', { class: versionChipRow }, chips))
            }

            // Determine which versions to render
            const selected = selectedVersions.get(rubric.id) || 'all'
            const versionsToShow = selected === 'all'
              ? versions
              : versions.filter(v => v.versionId === selected)

            if (versionsToShow.length === 0) {
              children.push(m('div', { style: `color: ${colors.textMuted}; font-size: ${font.size.sm};` },
                'No criteria defined'))
            } else {
              for (const ver of versionsToShow) {
                // Sub-header in "All" mode
                if (selected === 'all' && versions.length > 1) {
                  children.push(m('div', { class: versionSubHeader },
                    `v${ver.version} \u2014 ${ver.sessionCount} session${ver.sessionCount !== 1 ? 's' : ''}`))
                }
                if (ver.criteria.length === 0) {
                  children.push(m('div', { style: `color: ${colors.textMuted}; font-size: ${font.size.sm};` },
                    'No criteria defined'))
                } else {
                  for (const criterion of ver.criteria) {
                    children.push(m('div', { class: criterionRow }, [
                      m('div', {
                        class: critDot,
                        style: `background: ${CRITICALITY_COLORS[criterion.criticality] || colors.textMuted};`,
                        title: criterion.criticality,
                      }),
                      m('div', { class: critDesc, title: criterion.description }, criterion.description),
                      renderBar(criterion, rubric.id),
                      m('div', { class: countLabel },
                        GRADE_ORDER.reduce((sum, k) => sum + (criterion.grades[k] || 0), 0)),
                    ]))
                  }
                }
              }
            }
            sections.push(m('div', { class: rubricSection }, children))
          }

          return sections
        })(),
      ])
    },
  }
}
